/**
 * Data Fetch Service
 * Coordinates all data fetching strategies
 */

import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { IDataFetchStrategy } from '../strategies/data-fetch-strategy.interface';
import { ApiFetchStrategy } from '../strategies/api-fetch.strategy';
import { PatchFetchStrategy } from '../strategies/patch-fetch.strategy';
import { ApiPatchStrategy } from '../strategies/api-patch.strategy';
import { JSExecutorStrategy } from '../strategies/js-executor.strategy';
import { IDataFetchContext } from '../models';

/**
 * Service that manages and coordinates all data fetching strategies
 */
@Injectable({ providedIn: 'root' })
export class DataFetchService {
  private strategies: IDataFetchStrategy[] = [];
  
  constructor(
    apiFetch: ApiFetchStrategy,
    patchFetch: PatchFetchStrategy,
    apiPatch: ApiPatchStrategy,
    jsExecutor: JSExecutorStrategy
  ) {
    // Register built-in strategies
    this.registerStrategy(apiFetch);
    this.registerStrategy(patchFetch);
    this.registerStrategy(apiPatch);
    this.registerStrategy(jsExecutor);
    
    console.log('[DataFetchService] Initialized with strategies:', this.getRegisteredStrategies());
  }
  
  /**
   * Register a new data fetch strategy
   * @param strategy The strategy to register
   */
  registerStrategy(strategy: IDataFetchStrategy): void {
    if (!this.strategies.includes(strategy)) {
      this.strategies.push(strategy);
    }
  }
  
  /**
   * Unregister a strategy
   * @param strategy The strategy to remove
   */
  unregisterStrategy(strategy: IDataFetchStrategy): void {
    const index = this.strategies.indexOf(strategy);
    if (index > -1) {
      this.strategies.splice(index, 1);
    }
  }
  
  /**
   * Fetch data using the appropriate strategy
   * @param context The execution context
   * @returns Observable of the fetched data
   */
  fetchData(context: IDataFetchContext): Observable<any> {
    const dataFetchType = context.field.dataFetchType || '';
    
    if (!dataFetchType) {
      return of(null);
    }
    
    // Find matching strategy
    const strategy = this.strategies.find(s => s.canHandle(dataFetchType));
    
    if (!strategy) {
      console.warn(`[DataFetchService] No strategy found for data fetch type: "${dataFetchType}"`);
      return of(null);
    }
    
    // Execute strategy
    return strategy.execute(context);
  }
  
  /**
   * Setup field with the appropriate strategy
   * Initializes listeners, subscriptions, etc.
   * @param context The execution context
   */
  setupField(context: IDataFetchContext): void {
    const dataFetchType = context.field.dataFetchType || '';
    
    if (!dataFetchType) {
      return;
    }
    
    const strategy = this.strategies.find(s => s.canHandle(dataFetchType));
    
    if (strategy && strategy.setup) {
      strategy.setup(context);
    }
  }
  
  /**
   * Cleanup field resources
   * @param context The execution context
   */
  cleanupField(context: IDataFetchContext): void {
    const dataFetchType = context.field.dataFetchType || '';
    
    if (!dataFetchType) {
      return;
    }
    
    const strategy = this.strategies.find(s => s.canHandle(dataFetchType));
    
    if (strategy && strategy.cleanup) {
      strategy.cleanup(context);
    }
  }
  
  /**
   * Check if a strategy exists for the given data fetch type
   * @param dataFetchType The data fetch type to check
   * @returns true if a strategy exists
   */
  hasStrategy(dataFetchType: string): boolean {
    return this.strategies.some(s => s.canHandle(dataFetchType));
  }
  
  /**
   * Get the strategy for a given data fetch type
   * @param dataFetchType The data fetch type
   * @returns The strategy or undefined
   */
  getStrategy(dataFetchType: string): IDataFetchStrategy | undefined {
    return this.strategies.find(s => s.canHandle(dataFetchType));
  }
  
  /**
   * Get all registered strategy names
   * @returns Array of strategy names
   */
  getRegisteredStrategies(): string[] {
    return this.strategies.map(s => s.getName());
  }
  
  /**
   * Validate if context can be executed
   * @param context The execution context
   * @returns true if context is valid
   */
  validateContext(context: IDataFetchContext): boolean {
    const dataFetchType = context.field.dataFetchType || '';
    const strategy = this.strategies.find(s => s.canHandle(dataFetchType));
    
    if (!strategy) {
      return false;
    }
    
    if (strategy.validate) {
      return strategy.validate(context);
    }
    
    return true;
  }
}
